import axios, {
    AxiosInstance,
    InternalAxiosRequestConfig,
    AxiosError,
} from 'axios'
import { tokenManager } from './tokenManager'

export const AUTH_EXPIRED_MESSAGE_KEY = 'sfy_cms_auth_expired_msg'

export const publicApi = axios.create({
    baseURL: process.env.REACT_APP_API_ROOT_URL,
    withCredentials: true,
    headers: {
        'Accept': 'application/json',
    },
})

interface QueuedRequest {
    resolve: (value: unknown) => void
    reject: (reason: unknown) => void
    config: InternalAxiosRequestConfig
}

function isAuthFailure(error: AxiosError): boolean {
    if (error.response?.status === 401) return true

    if (error.response?.status === 500) {
        const data = error.response.data as Record<string, unknown> | undefined
        const msg = (data?.message as string) ?? ''
        return msg.toLowerCase().includes('unauthenticated')
    }

    return false
}

class ApiClient {
    readonly instance: AxiosInstance
    private isRefreshing = false
    private requestQueue: QueuedRequest[] = []
    private onAuthFailure: (() => void) | null = null
    private onForbidden: (() => void) | null = null
    private refreshTokenFn: (() => Promise<string | null>) | null = null

    constructor() {
        this.instance = axios.create({
            baseURL: process.env.REACT_APP_API_ROOT_URL,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            withCredentials: true,
        })

        this.setupInterceptors()
    }

    setRefreshTokenFn(fn: () => Promise<string | null>): void {
        this.refreshTokenFn = fn
    }

    setOnAuthFailure(fn: () => void): void {
        this.onAuthFailure = fn
    }

    setOnForbidden(fn: () => void): void {
        this.onForbidden = fn
    }

    private async attemptRefresh(): Promise<string | null> {
        if (this.refreshTokenFn) {
            const token = await this.refreshTokenFn()
            if (token) return token
        }

        const response = await publicApi.get('/auth/refresh-token')
        const data = response.data as {
            success?: boolean
            data?: { token?: string; expires_in?: number }
        }

        if (data?.success && data.data?.token) {
            tokenManager.setToken(
                data.data.token,
                data.data.expires_in ?? 1200
            )
            return data.data.token
        }

        return null
    }

    private setupInterceptors(): void {
        this.instance.interceptors.request.use(
            (config) => {
                const token = tokenManager.getAccessToken()
                if (token && !config.headers['Authorization']) {
                    config.headers['Authorization'] = `Bearer ${token}`
                }
                return config
            },
            (error) => Promise.reject(error)
        )

        this.instance.interceptors.response.use(
            (response) => response,
            async (error: AxiosError) => {
                const originalRequest = error.config as
                    | (InternalAxiosRequestConfig & { _retry?: boolean })
                    | undefined

                if (!originalRequest) return Promise.reject(error)

                if (error.response?.status === 403) {
                    this.onForbidden?.()
                    return Promise.reject(error)
                }

                if (isAuthFailure(error) && !originalRequest._retry) {
                    originalRequest._retry = true

                    if (this.isRefreshing) {
                        return new Promise((resolve, reject) => {
                            this.requestQueue.push({
                                resolve,
                                reject,
                                config: originalRequest,
                            })
                        })
                    }

                    this.isRefreshing = true

                    try {
                        const newToken = await this.attemptRefresh()

                        if (newToken) {
                            this.processQueue(newToken)
                            originalRequest.headers['Authorization'] =
                                `Bearer ${newToken}`
                            return this.instance(originalRequest)
                        } else {
                            this.failQueue(new Error('Token refresh failed'))
                            this.onAuthFailure?.()
                            return Promise.reject(error)
                        }
                    } catch (refreshError) {
                        this.failQueue(refreshError as Error)
                        this.onAuthFailure?.()
                        return Promise.reject(refreshError)
                    } finally {
                        this.isRefreshing = false
                    }
                }

                return Promise.reject(error)
            }
        )
    }

    private processQueue(token: string): void {
        this.requestQueue.forEach(({ resolve, config }) => {
            config.headers['Authorization'] = `Bearer ${token}`
            resolve(this.instance(config))
        })
        this.requestQueue = []
    }

    private failQueue(error: Error): void {
        this.requestQueue.forEach(({ reject }) => reject(error))
        this.requestQueue = []
    }
}

export const apiClient = new ApiClient()

export const api = apiClient.instance
